/**
 * Weekly roadmap goals and reminders.
 *
 * A goal is the learner's own commitment — "two steps a week" — not something
 * the engine decides. Stored per roadmap so a Journey target and a Learning
 * target can differ. `updatedAt` lets two devices agree on the newer setting.
 */
import { thisWeek } from "@/lib/roadmapProgress";
import { cloudStorage } from "@/lib/cloudStore";

const KEY = "wcc360.roadmap_goals";

export interface RoadmapGoal {
  roadmapId: string;
  /** Steps the learner wants to finish each week. 1–7. */
  weeklySteps: number;
  /** Optional minutes-per-week target. 0 means "not set". */
  weeklyMinutes: number;
  /** Whether we nudge them when the week is running out. */
  remindersOn: boolean;
  /** 0 = Sunday … 6 = Saturday. The day we check in. */
  reminderDay: number;
  /** ISO date (YYYY-MM-DD) of the last nudge shown, so we only nudge once a day. */
  lastRemindedOn: string | null;
  updatedAt: string;
}

export const DEFAULT_GOAL: Omit<RoadmapGoal, "roadmapId" | "updatedAt"> = {
  weeklySteps: 2,
  weeklyMinutes: 0,
  remindersOn: true,
  reminderDay: 5, // Friday
  lastRemindedOn: null,
};

export function readGoals(): RoadmapGoal[] {
  try {
    const raw = cloudStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? (arr as RoadmapGoal[]) : [];
  } catch {
    return [];
  }
}

export function writeGoals(list: RoadmapGoal[]): void {
  try {
    cloudStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage full or unavailable — the UI stays usable */
  }
}

export function getGoal(roadmapId: string): RoadmapGoal {
  const found = readGoals().find((g) => g.roadmapId === roadmapId);
  return found ?? { roadmapId, ...DEFAULT_GOAL, updatedAt: new Date(0).toISOString() };
}

export function saveGoal(
  roadmapId: string,
  patch: Partial<Omit<RoadmapGoal, "roadmapId" | "updatedAt">>,
): RoadmapGoal {
  const current = getGoal(roadmapId);
  const next: RoadmapGoal = {
    ...current,
    ...patch,
    weeklySteps: Math.max(1, Math.min(7, patch.weeklySteps ?? current.weeklySteps)),
    weeklyMinutes: Math.max(0, Math.min(1200, patch.weeklyMinutes ?? current.weeklyMinutes)),
    roadmapId,
    updatedAt: new Date().toISOString(),
  };
  writeGoals([...readGoals().filter((g) => g.roadmapId !== roadmapId), next]);
  return next;
}

export interface GoalStatus {
  goal: RoadmapGoal;
  steps: number;
  minutes: number;
  /** 0–100, rounded. Presentation only. */
  percent: number;
  met: boolean;
  /** Days left in the week, including today. */
  daysLeft: number;
  /** True when a nudge is due today: reminders on, target not met, day reached. */
  reminderDue: boolean;
}

export function goalStatus(roadmapId: string, now = new Date()): GoalStatus {
  const goal = getGoal(roadmapId);
  const { steps, minutes } = thisWeek(roadmapId);
  const stepPart = goal.weeklySteps > 0 ? steps / goal.weeklySteps : 1;
  const minutePart = goal.weeklyMinutes > 0 ? minutes / goal.weeklyMinutes : 1;
  const ratio = goal.weeklyMinutes > 0 ? Math.min(stepPart, minutePart) : stepPart;
  const met = steps >= goal.weeklySteps && (goal.weeklyMinutes === 0 || minutes >= goal.weeklyMinutes);
  const dayOfWeek = now.getDay();
  const daysLeft = 7 - ((dayOfWeek + 6) % 7); // Monday-start week
  const today = now.toISOString().slice(0, 10);
  const reminderDue =
    goal.remindersOn &&
    !met &&
    goal.lastRemindedOn !== today &&
    (dayOfWeek === goal.reminderDay || daysLeft <= 2);
  return {
    goal,
    steps,
    minutes,
    percent: Math.min(100, Math.round(ratio * 100)),
    met,
    daysLeft,
    reminderDue,
  };
}

/** Marks today's nudge as seen so it doesn't come back until tomorrow. */
export function dismissReminder(roadmapId: string): void {
  saveGoal(roadmapId, { lastRemindedOn: new Date().toISOString().slice(0, 10) });
}

export const DAY_LABEL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
